import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Check, ChevronRight, Ticket } from 'lucide-react';
import useGiftCard from '../hooks/useGiftCard';
import usePromoCode from '../../../hooks/order/usePromoCode';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useBasket from '../../../hooks/basket/useBasket';
import useGetPromoCodesApi from '../../../hooks/api/companies/useGetPromoCodesApi';
import MinimumValueErrorLabel from '../../../components-library/labels/MinimumValueErrorLabel';
import { selectCachedOrder } from '../../../store/slices/OrderSlice';
import { isPromoMinimumNotReached } from './promo-minimum';

interface Props {
  className?: string;
}

const PromoOrGiftCardRow = ({ className }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:GiftCards:PromoOrGiftCardRow' });
  const { toggleAddPromoOrGiftCardModal } = useAppMethods();
  const { giftCard, remove: removeGiftCard } = useGiftCard();
  const { appliedPromoCode, removePromoCode } = usePromoCode();
  const { data: promoCodes } = useGetPromoCodesApi();

  const appliedLabel = appliedPromoCode ?? (giftCard ? `${giftCard.code.slice(0, 4)}...` : null);
  const availableCount = promoCodes?.length ?? 0;

  // Works for any applied promo, listed or typed by hand: the dialog's Applied toggle only exists for
  // listed master codes, so this is the one remove control a campaign code has.
  const onRemove = (): void => (appliedPromoCode ? removePromoCode() : removeGiftCard());

  return (
    <div className={className}>
      {/*
        `data-applied` is the machine-readable half of the tick + truncated-code badge next to this
        row. The visible label is `${code.slice(0, 4)}...` for a gift card and the raw code for a promo
        code, so a spec asserting on it would be asserting on a display truncation; `data-applied-kind`
        additionally says WHICH of the two is on the order, which the badge alone cannot.
      */}
      <div className="flex items-center gap-sm">
        <button
          type="button"
          data-test="promo-or-gift-card-row"
          data-applied={appliedLabel ? 'true' : 'false'}
          data-applied-kind={appliedPromoCode ? 'promo_code' : giftCard ? 'gift_card' : undefined}
          onClick={() => toggleAddPromoOrGiftCardModal({ openModal: true })}
          className={rowStyle}
        >
          <span className="flex items-center gap-xs min-w-0">
            <Ticket className="w-4 h-4 text-brand-text-primary shrink-0" />
            <span className="underline decoration-muted-foreground/40 text-brand-text-primary truncate">
              {t('title')}
            </span>
            {!appliedLabel && availableCount > 0 && <span className={badgeStyle}>{availableCount}</span>}
          </span>
          {appliedLabel ? (
            <span className="flex items-center gap-2xs text-xs font-semibold text-success shrink-0">
              <Check className="w-3.5 h-3.5" />
              {appliedLabel}
            </span>
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          )}
        </button>
        {appliedLabel && (
          <button type="button" data-test="promo-or-gift-card-remove" onClick={onRemove} className={removeStyle}>
            {t('buttons.remove')}
          </button>
        )}
      </div>
      <MinValueErrorLabel />
    </div>
  );
};

const MinValueErrorLabel = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Checkout:PromoCodeInput' });
  const { formatCurrency } = useAppMethods();
  const cachedOrder = useSelector(selectCachedOrder);
  // Compare against the smart-priced subtotal (matches the backend's minimum check), NOT the
  // menu-price `subtotal` (Σ original_price) which is higher and hid this notice (PROD-407).
  const { actualSubtotal } = useBasket();

  if (
    !cachedOrder?.coupon_details?.components?.length ||
    !isPromoMinimumNotReached(cachedOrder.coupon_details.components, actualSubtotal)
  ) {
    return null;
  }

  return (
    <MinimumValueErrorLabel
      description={t('feedback.usage_threshold')}
      value={formatCurrency(cachedOrder?.coupon_details?.components[0]?.usage_threshold)}
      className="mt-xs"
      type="Notice"
    />
  );
};

const rowStyle = 'flex items-center justify-between flex-1 min-w-0 text-sm gap-sm';

const removeStyle = 'text-xs font-semibold text-danger underline shrink-0 hover:opacity-80';

const badgeStyle =
  'text-[10px] font-semibold bg-brand-primary/15 text-brand-primary rounded-full px-[6px] py-[2px] leading-none';

export default PromoOrGiftCardRow;
